import React, { Fragment, useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react'
import { format, add, getYear, getWeek, nextMonday, nextTuesday, nextWednesday, nextSaturday, nextSunday, isWednesday } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { customerStore } from '../../stores'
import { DeliveryZonesService, IDeliveryDay } from '../../services'
import { collectionsStore } from '../collections/collections.store'
import { cartStore, filterBarStore } from '../'

import { DeliveryZoneDate } from '../../classes/delivery-zone-date.class'
import { collectionListStore } from '../collections/collection-list/collection-list.store'

import './delivery-day-picker.scss'

enum DeliveryDay {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  SATURDAY = 'Saturday',
  SUNDAY = 'Sunday'
}

const nextDeliveryDayMap = {
  [DeliveryDay.MONDAY]: nextMonday,
  [DeliveryDay.TUESDAY]: nextTuesday,
  [DeliveryDay.WEDNESDAY]: nextWednesday,
  [DeliveryDay.SATURDAY]: nextSaturday,
  [DeliveryDay.SUNDAY]: nextSunday
}

const daysNumOfWeek = 7

const cutoffDayMap = {
  [DeliveryDay.MONDAY]: 'Wednesday',
  [DeliveryDay.TUESDAY]: 'Wednesday',
  [DeliveryDay.WEDNESDAY]: 'Wednesday',
  [DeliveryDay.SATURDAY]: 'Monday',
  [DeliveryDay.SUNDAY]: 'Tuesday'
}

export const findDeliveryDay = (days: IDeliveryDay[], preferredDay: string): number => {
  if (!preferredDay || days.length <= 1) {
    return 0
  }

  // First try to find an exact match
  const exactMatchIndex = days.findIndex((day) => day?.delivery_day?.toLowerCase() === preferredDay.toLowerCase())

  if (exactMatchIndex !== -1) {
    return exactMatchIndex
  }

  return findNextAvailableDeliveryDay(days, preferredDay)
}

// Helper function to get day of week as number (0 = Sunday, 1 = Monday, etc.)
export const getDayOfWeekNumber = (dayName: string): number => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const dayIndex = days.indexOf(dayName.toLowerCase())
  return dayIndex !== -1 ? dayIndex : 0
}

export const findNextAvailableDeliveryDay = (days: IDeliveryDay[], preferredDay: string): number => {
  if (days.length === 0) {
    return 0
  }
  const preferredDayNumber = getDayOfWeekNumber(preferredDay)
  // If preferred day is after the last delivery day, return the last index
  if (preferredDayNumber > getDayOfWeekNumber(days[days.length - 1].delivery_day)) {
    return days.length - 1
  }

  const daysMap = new Map(days.map((day, index) => [getDayOfWeekNumber(day.delivery_day), index]))
  // try to find the next available delivery day
  for (let i = 1; i < daysNumOfWeek; i++) {
    const next = (preferredDayNumber + i) % daysNumOfWeek
    if (daysMap.has(next)) {
      return daysMap.get(next)
    }
  }

  return 0
}

export const getDeliveryDate = (date: Date, overrideDeliveryDate: string) => {
  const todayInChicago = toZonedTime(date, 'America/Chicago')
  const isCutOffDay = isWednesday(todayInChicago)
  const initialDate = add(todayInChicago, { weeks: isCutOffDay ? 0 : 1 })
  return nextDeliveryDayMap[overrideDeliveryDate as DeliveryDay]?.(initialDate) ?? initialDate
}

export const storeDeliveryDate = ({
  selectedDay,
  selectedDeliveryDayIndex
}: {
  selectedDay: IDeliveryDay
  selectedDeliveryDayIndex: number
}) => {
  sessionStorage.setItem('selectedDeliveryDate', JSON.stringify(selectedDay))
  sessionStorage.setItem('selectedDeliveryDateIndex', selectedDeliveryDayIndex.toString())

  const deliveryTime = sessionStorage.getItem('selectedDeliveryTime')
  if (deliveryTime) {
    cartStore.setDeliveryTime(JSON.parse(deliveryTime))
  }

  if (selectedDay) {
    cartStore.deliveryDate = selectedDay.delivery_date
    cartStore.deliveryWeek = selectedDay.week
    cartStore.shippingMethod = selectedDay.delivery_region_name

    collectionsStore.updateMenuForDelivery(selectedDay.week)
    collectionsStore.selectedDeliveryWeek = selectedDay.week
  }

  const { currentWeeklyMenu } = collectionsStore
  if (currentWeeklyMenu) {
    const products = collectionListStore.collections(currentWeeklyMenu.handle)
    filterBarStore.organizeFilters(products)
  }

  // set the delivery time to the first one
  if (selectedDay?.delivery_times.length > 0) {
    const deliveryTime = cartStore.formatDeliveryTime(
      selectedDay.delivery_date,
      selectedDay.delivery_times[0].start_time,
      selectedDay.delivery_times[0].end_time
    )

    cartStore.setDeliveryTime(deliveryTime)
  }
}

export const DeliveryDayPicker: React.FC = observer(() => {
  const [deliveryDays, setDeliveryDays] = useState<IDeliveryDay[]>([])
  const [selectedDeliveryDayIndex, setSelectedDeliveryDayIndex] = useState<number>(0)
  const [deliveryWeek, setDeliveryWeek] = useState<string>('')
  const [deliveryDate, setDeliveryDate] = useState<string>('')
  const [cutoffDay, setCutoffDay] = useState<string>('')
  const [isLoadingDeliveryDays, setIsLoadingDeliveryDays] = useState<boolean>(true)
  const [showDeliveryOptions, setShowDeliveryOptions] = useState<boolean>(false)
  const modalRef = useRef(null)
  const calendarIconRef = useRef(null)

  const { zip: postalCode } = customerStore.defaultAddress || {}
  const preferredDayInWeek = collectionsStore.customerPreferredDeliveryDay
  const overrideDeliveryDate = collectionsStore.customerOverrideDeliveryDay
  const overrideDeliveryRegion = collectionsStore.customerOverrideDeliveryRegion

  const handleClickOutside = (e: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target) && !calendarIconRef.current.contains(e.target)) {
      setShowDeliveryOptions(false)
    }
  }

  useEffect(() => {
    if (showDeliveryOptions) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDeliveryOptions])

  useEffect(() => {
    if (overrideDeliveryDate) {
      setSelectedDeliveryDayIndex(0)
      const deliveryDate = getDeliveryDate(new Date(), overrideDeliveryDate)
      const deliveryDateFormatted = format(deliveryDate, 'eeee, MMMM do')
      setDeliveryDate(deliveryDateFormatted)

      const cutoffDay = cutoffDayMap[overrideDeliveryDate as DeliveryDay]
      setCutoffDay(cutoffDay)

      const deliveryWeek = getWeek(deliveryDate).toString()
      setDeliveryWeek(deliveryWeek)

      const selectedDay: IDeliveryDay = {
        delivery_region_name: overrideDeliveryRegion,
        delivery_area_name: overrideDeliveryRegion,
        option_handle: overrideDeliveryRegion,
        delivery_day: overrideDeliveryDate.toUpperCase(),
        week: `${getYear(deliveryDate).toString()}-W${getWeek(deliveryDate).toString()}`,
        delivery_date: format(deliveryDate, 'yyyy-MM-dd'),
        cutoff_date: format(toZonedTime(new Date(), 'America/Chicago'), 'yyyy-MM-dd'),
        delivery_times: [
          {
            start_time: '08:00:00',
            end_time: '20:01:00'
          }
        ],
        cutoff: 0,
        tracking_cutoff_hours: 0,
        surcharge_price: 0
      }

      storeDeliveryDate({
        selectedDay: selectedDay,
        selectedDeliveryDayIndex: 0
      })

      setIsLoadingDeliveryDays(false)
    } else {
      const fetchDeliveryDays = async () => {
        const deliveryService = new DeliveryZonesService()
        const days = (await deliveryService.deliveryDays(postalCode)) as IDeliveryDay[]

        if (days.length === 0) {
          return
        }

        setDeliveryDays(days)

        const selectedIndex = findDeliveryDay(days, preferredDayInWeek)
        setSelectedDeliveryDayIndex(selectedIndex)

        const selectedDay = days[selectedIndex ?? 0]?.delivery_date
        const deliveryWeek = days[selectedIndex ?? 0]?.week?.split('W')[1] ?? ''
        setDeliveryWeek(deliveryWeek)

        const cutoffDay = new DeliveryZoneDate(days[selectedIndex ?? 0]?.cutoff_date).day
        setCutoffDay(cutoffDay)

        const date = new DeliveryZoneDate(selectedDay)?.deliveryDate
        const deliveryDate = !isNaN(date.getTime()) ? format(date, 'eeee, MMMM do') : ''
        setDeliveryDate(deliveryDate)

        storeDeliveryDate({ selectedDay: days[selectedIndex], selectedDeliveryDayIndex: selectedIndex })
        setIsLoadingDeliveryDays(false)
      }
      fetchDeliveryDays()
    }
  }, [postalCode, preferredDayInWeek, overrideDeliveryDate, overrideDeliveryRegion])

  useEffect(() => {
    const selectedDay = deliveryDays[selectedDeliveryDayIndex]

    if (!selectedDay) {
      return
    }

    const deliveryDate = new DeliveryZoneDate(selectedDay.delivery_date)?.deliveryDate
    const formattedDeliveryDate = format(deliveryDate, 'eeee, MMMM do')

    setDeliveryDate(formattedDeliveryDate)

    storeDeliveryDate({ selectedDay, selectedDeliveryDayIndex })

    const cutoffDay = new DeliveryZoneDate(selectedDay.cutoff_date)?.day
    setCutoffDay(cutoffDay)
  }, [selectedDeliveryDayIndex, setDeliveryDate, setCutoffDay])

  return (
    <div className='date-picker-container'>
      {isLoadingDeliveryDays ? (
        <h2>Checking delivery day options...</h2>
      ) : (
        <Fragment>
          <div className='date-headline'>Menu for Week {deliveryWeek}</div>
          <div className='date-info'>
            <div className='order-text'>Your order will arrive on</div>
            <div className='delivery-date'>
              <strong>{deliveryDate}</strong>
              {deliveryDays.length > 0 && (
                <button
                  className='calendar-icon'
                  role='img'
                  aria-label='calendar'
                  ref={calendarIconRef}
                  onClick={() => {
                    setShowDeliveryOptions(!showDeliveryOptions)
                  }}
                >
                  Change
                </button>
              )}
            </div>
            {showDeliveryOptions && (
              <div id='delivery-options' className='delivery-options' ref={modalRef}>
                {deliveryDays.map((day, i) => {
                  const delivery: DeliveryZoneDate = new DeliveryZoneDate(day.delivery_date)
                  return (
                    <div
                      className='delivery-option'
                      key={day.delivery_day}
                      onClick={() => {
                        setSelectedDeliveryDayIndex(i)
                        setShowDeliveryOptions(!showDeliveryOptions)
                      }}
                    >
                      <span className={`${selectedDeliveryDayIndex === i ? 'selected' : ''}`}>
                        {delivery.dayRelative}, {delivery.dateMinimal}
                      </span>
                      {selectedDeliveryDayIndex === i && (
                        <img src='https://cdn.shopify.com/s/files/1/0722/4459/2929/files/checkmark-icon.svg' />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
          <div className='order-badge'>Order by {cutoffDay}</div>
        </Fragment>
      )}
    </div>
  )
})

export default DeliveryDayPicker
